import React, { useCallback, useMemo, useRef } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import useToggle from 'hooks/useToggle'
import { pluralizeToString } from 'lib/string/pluralize'
import FormatCurrency from 'components/Common/FormatCurrency'
import SmallToggleButton from 'components/Luxkit/Button/SmallToggleButton'
import Heading from 'components/Luxkit/Typography/Heading'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { getDepartureCheapestOptionForTravellers } from 'lib/tours/tourDepartureUtils'
import LineInfoCircleIcon from 'components/Luxkit/Icons/line/LineInfoCircleIcon'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Divider from 'components/Luxkit/Divider'
import config from 'constants/config'
import { useAppSelector } from 'hooks/reduxHooks'
import Group from 'components/utils/Group'
import DropdownList from 'components/Luxkit/Dropdown/List/DropdownList'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { fillArray, nonNullable, sum } from 'lib/array/arrayUtils'
import generateDepartureRoomPriceBreakdown from 'lib/tours/generateDepartureRoomPriceBreakdown'
import MessageBanner from 'components/Luxkit/Banners/MessageBanner'
import { formatRoomType } from 'lib/tours/tourUtils'
import { isSalesforceTourV2 } from 'lib/offer/offerUtils'
import useAgentHubCommission from 'agentHub/hooks/useAgentHubCommissionV2'

const BreakdownList = styled(VerticalSpacer)`
  padding-inline: ${rem(16)};
`

interface RoomOptionView {
  room: App.Occupants;
  breakdown: App.Tours.V2OfferDeparturePriceBreakdown;
  option: App.Tours.TourV2OfferPurchasableOption;
}

interface Props {
  offer: App.Tours.TourV2Offer;
  variation: App.Tours.TourV2OfferVariation;
  travellers: Array<App.Occupants> | App.Occupants;
  departure: App.Tours.TourV2OfferDeparture;
  packageType?: string;
}

function TourV2DeparturePricingDropdown(props: Props) {
  const {
    travellers,
    offer,
    departure,
    packageType,
  } = props

  const triggerRef = useRef<HTMLButtonElement>(null)
  const [isDropdownOpen, toggleDropdown, , closeDropdown] = useToggle()

  const canViewLuxPlusBenefits = useAppSelector(getCanViewLuxPlusBenefits)

  const roomOptions = useMemo<Array<RoomOptionView>>(() => {
    const rooms = Array.isArray(travellers) ? travellers : [travellers]
    return nonNullable(rooms.map(room => {
      const cheapestOption = getDepartureCheapestOptionForTravellers(departure, room, packageType)
      if (cheapestOption) {
        const breakdown = generateDepartureRoomPriceBreakdown(room, cheapestOption, offer)
        return {
          room,
          breakdown,
          option: cheapestOption,
        }
      }
    }))
  }, [departure, offer, travellers, packageType])

  const totalPrice = useMemo(() => {
    return sum(roomOptions, option => canViewLuxPlusBenefits ? option.breakdown.totalMemberPrice || option.breakdown.totalPrice : option.breakdown.totalPrice)
  }, [canViewLuxPlusBenefits, roomOptions])

  const handleToggleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault()
    event.stopPropagation()
    toggleDropdown()
  }, [toggleDropdown])

  const { commissionPercentage } = useAgentHubCommission(offer.id)
  const customerView = useAppSelector(state => state.agentHub.customerView)
  const showAgentView = config.agentHub.isEnabled && !customerView && !!commissionPercentage
  const commissionTotal = totalPrice * (commissionPercentage / 100)

  return (
    <>
      <SmallToggleButton
        on={isDropdownOpen}
        onClick={handleToggleClick}
        ref={triggerRef}
        size="small"
      >
        Price details
      </SmallToggleButton>
      <DropdownList
        size="S"
        open={isDropdownOpen}
        placement="bottom-start"
        title="Price details"
        anchorRef={triggerRef}
        triggerRef={triggerRef}
        onClose={closeDropdown}
      >
        <BreakdownList gap={8}>
          {roomOptions.map(
            (roomOption, index) => (
              <VerticalSpacer key={index} gap={4}>
                <Group direction="horizontal" horizontalAlign="space-between">
                  <BodyText variant="medium" weight="bold" forwardedAs="span">{formatRoomType(roomOption.option.roomType)}</BodyText>
                  <BodyText variant="medium" weight="bold" forwardedAs="span">
                    <FormatCurrency value={canViewLuxPlusBenefits ? roomOption.breakdown.totalMemberPrice || roomOption.breakdown.totalPrice : roomOption.breakdown.totalPrice} />
                  </BodyText>
                </Group>
                {fillArray(roomOption.room.adults).map((_, index) => (
                  <Group key={index} direction="horizontal" horizontalAlign="space-between">
                    <BodyText variant="medium" forwardedAs="span">{isSalesforceTourV2(offer) ? 'Traveller' : 'Adult'} {index + 1}</BodyText>
                    <BodyText variant="medium" forwardedAs="span">
                      <FormatCurrency value={canViewLuxPlusBenefits ? roomOption.breakdown.memberPricePerAdult || roomOption.breakdown.pricePerAdult : roomOption.breakdown.pricePerAdult} />
                    </BodyText>
                  </Group>),
                )}
                {fillArray(roomOption.room.children ?? 0).map((_, index) => (
                  <Group key={index} direction="horizontal" horizontalAlign="space-between">
                    <BodyText variant="medium" forwardedAs="span">Child</BodyText>
                    <BodyText variant="medium" forwardedAs="span">
                      <FormatCurrency value={roomOption.breakdown.pricePerChild} />
                    </BodyText>
                  </Group>),
                )}
                {roomOption.breakdown.childrenPriceBreakdown.length >
                  roomOption.breakdown.appliedChildDiscounts && (
                    <MessageBanner
                      kind="informational-primary"
                      icon={<LineInfoCircleIcon />}
                      description={<>
                        Child discount applies to{' '}
                        {pluralizeToString(
                          'child',
                          roomOption.breakdown.appliedChildDiscounts,
                        )}{' '}
                        only, remainder will be charged as an adult.
                      </>}
                    />
                )}
              </VerticalSpacer>
            ),
          )}
          <Divider kind="secondary" direction="horizontal" />
          {!showAgentView && <Heading variant="heading6">
            <Group direction="horizontal" horizontalAlign="space-between">
              <div>Total</div>
              <FormatCurrency value={totalPrice} />
            </Group>
          </Heading>}
          {showAgentView && <VerticalSpacer gap={4}>
            <Group direction="horizontal" horizontalAlign="space-between">
              <BodyText variant="medium" weight="bold">Total commission ({commissionPercentage}%)</BodyText>
              <BodyText variant="medium" weight="bold" colour="highlight-secondary">
                -<FormatCurrency value={commissionTotal} />
              </BodyText>
            </Group>
            <Group direction="horizontal" horizontalAlign="space-between">
              <BodyText variant="medium" weight="bold">Gross total</BodyText>
              <BodyText variant="medium" weight="bold">
                <FormatCurrency value={totalPrice} />
              </BodyText>
            </Group>
            <Group direction="horizontal" horizontalAlign="space-between">
              <BodyText variant="medium" weight="bold">Net total</BodyText>
              <BodyText variant="medium" weight="bold">
                <FormatCurrency value={totalPrice - commissionTotal} />
              </BodyText>
            </Group>
          </VerticalSpacer>}
        </BreakdownList>
      </DropdownList>
    </>
  )
}

export default TourV2DeparturePricingDropdown
